<script setup>
import { ref, computed, watch, defineModel } from "vue";
import * as PRODUCT from "../composables/useProducts"
import petTypeSelect from "./VPetTypeSelect.vue";
import vBaseInput from "../Inputs/VBaseInput.vue";

const emits = defineEmits(["update:modelValue"]);

const petType = defineModel("petType", {
    type: String
});

const petName = defineModel("petName", {
    type: String,
    default: "",
});

const products = ref([
    {
        image: "/images/nexgard-combo.png",
        name: "NexGard Combo",
        slug: PRODUCT.NEXGARD_COMBO,
        width: 87,
        height: 29,
        active: false,
        type: "cat",
        row: 1,
        order: 1,
    },
    {
        image: "/images/frontline-plus.png",
        name: "Frontline Plus",
        slug: PRODUCT.FRONTLINE_PLUS,
        width: 127,
        height: 23,
        active: false,
        type: "cat",
        row: 1,
        order: 2,
    },
    {
        image: "/images/nexgard.png",
        name: "NexGard",
        slug: PRODUCT.NEXGARD,
        width: 104,
        height: 17,
        active: false,
        type: "dog",
        row: 1,
        order: 1,
    },
    {
        image: "/images/nexgard-spectra.png",
        name: "NexGard Spectra",
        slug: PRODUCT.NEXGARD_SPECTRA,
        width: 97,
        height: 29,
        active: false,
        type: "dog",
        row: 1,
        order: 2,
    },
    {
        image: "/images/heartgard.png",
        name: "Heartgard",
        slug: PRODUCT.HEARTGARD,
        width: 109,
        height: 31,
        active: false,
        type: "dog",
        row: 2,
        order: 2,
    },
    {
        image: "/images/frontline-plus.png",
        name: "Frontline Plus",
        slug: PRODUCT.FRONTLINE_PLUS,
        width: 127,
        height: 23,
        active: false,
        type: "dog",
        row: 2,
        order: 1,
    },
    {
        image: "/images/recombitek.png",
        name: "Recombitek",
        slug: PRODUCT.RECOMBITEK,
        width: 94,
        height: 27,
        active: false,
        type: "dog",
        row: 2,
        order: 3,
    },
])
const selectProduct = product => {
    products.value.forEach(p => p.active = false);
    product.active = true;

    // Emit the selected product to parent component
    emits('update:modelValue', { name: product.name, slug: product.slug, type: product.type });
}

const isProductSelected = computed(() => {
    return products.value.some(p => p.active);
})

const petTypeWatcher = watch(petType, () => {
    // Reset product selection when pet type changes
    products.value.forEach(p => p.active = false);

    // Emit null to indicate no product is selected
    emits('update:modelValue', null);
});

const productRows = computed(() => {
    const filtered = products.value
        .map((product, index) => ({
            product,
            index,
            row: product.row ?? 1,
            order: product.order ?? index,
        }))
        .filter(({ product }) => product.type === petType.value)
        .sort((left, right) => {
            if (left.row !== right.row) {
                return left.row - right.row;
            }

            if (left.order !== right.order) {
                return left.order - right.order;
            }

            return left.index - right.index;
        });

    const rows = new Map();

    filtered.forEach(({ product, row }) => {
        if (!rows.has(row)) {
            rows.set(row, []);
        }

        rows.get(row).push(product);
    });

    return Array.from(rows.entries()).map(([row, products]) => ({
        row,
        products,
    }));
});

</script>

<template>
    <div>
        <petTypeSelect v-model="petType"/>

        <v-base-input id="petName" :key="'petname'" :label="$t('form.petName')" theme="dark"
                    v-model="petName" class="mb-6"></v-base-input>

        <p class="block text-base font-medium text-white mb-1" v-html="$t('form.product')"></p>

        <div class="bg-bi-light-gray p-8 rounded-md" v-auto-animate
            :class="{ 'product-selected': isProductSelected }">

            <div v-for="productRow in productRows" :key="`row-${productRow.row}`"
                class="flex flex-wrap justify-center gap-10 items-center"
                :class="{ 'mb-10': productRow !== productRows[productRows.length - 1] }">
                <div class="text-center basis-full sm:basis-1/5" v-for="product in productRow.products" :key="product.slug + '-' + product.type">
                    <a href="#" tabindex="0" @click.prevent="selectProduct(product)" :aria-label="product.name">
                        <img :src="product.image" :alt="product.name" :width="product.width" :height="product.height"
                            :class="[product.name.toLowerCase().replace(' ', '-'), { active: product.active }]">
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
img {
    @apply mx-auto transition-all duration-150 ease-in-out cursor-pointer;

    &.active {
        @apply opacity-100 filter brightness-100;
    }

    &.metacam {
        margin-top: -17px;

        @media (max-width: 639px) {
            margin-top: 0;
        }
    }

    &.nexgard-combo {
        @media (max-width: 639px) {
            width: 96px;
            height: auto;
        }
    }
}

.product-selected img:not(.active) {
    @apply opacity-30 filter brightness-0;
}
</style>
